import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, CalendarPlus, Search } from "lucide-react";

import { eur } from "@/lib/format";
const cliName = (p) => [p.cliente?.nome, p.cliente?.cognome].filter(Boolean).join(" ").trim() || "Cliente senza nome";

export default function QuickReminderDialog({ pratiche = [], onClose }) {
  const qc = useQueryClient();
  const [praticaId, setPraticaId] = useState("");
  const [query, setQuery] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const [ev, setEv] = useState({ titolo: "", data: "", descrizione: "", avvisa: 7 });
  const [saving, setSaving] = useState(false);

  const selected = pratiche.find((p) => p.id === praticaId);
  const q = query.trim().toLowerCase();
  const filtered = (q
    ? pratiche.filter((p) => `${cliName(p)} ${p.tipo_pratica_nome || ""}`.toLowerCase().includes(q))
    : pratiche
  ).slice(0, 40);

  const selectPratica = (p) => {
    setPraticaId(p.id);
    setListOpen(false);
    setQuery("");
    setEv((e) => ({ ...e, titolo: `${cliName(p)} — ${p.tipo_pratica_nome || ""}`.trim() }));
  };

  const clearPratica = () => {
    setPraticaId("");
    setQuery("");
    setListOpen(true);
  };

  const save = async () => {
    if (!praticaId) return toast.error("Seleziona una pratica");
    if (!ev.data) return toast.error("Imposta data e ora");
    setSaving(true);
    try {
      await api.post(`/pratiche/${praticaId}/evento`, {
        titolo: ev.titolo || "Promemoria", data: ev.data + ":00",
        descrizione: ev.descrizione, avvisa_giorni: Number(ev.avvisa) || null,
      });
      toast.success("Promemoria creato su Google Calendar");
      qc.invalidateQueries({ queryKey: ["calendar-events"] });
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nella creazione"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="quick-reminder-dialog">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-4 border-t-2 border-[#C9A84C] shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl text-[#1A2B4A] flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-[#C9A84C]" /> Nuovo promemoria</h3>
          <button data-testid="qr-close" onClick={onClose} className="text-[#4a5568] hover:text-[#1A2B4A]"><X className="w-5 h-5" /></button>
        </div>

        <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Pratica collegata</label>
        <div className="relative mt-1 mb-4">
          <div className="flex items-center border border-[#1A2B4A]/20 rounded-sm px-3 focus-within:border-[#C9A84C]">
            <Search className="w-4 h-4 text-[#4a5568] shrink-0" />
            <input data-testid="qr-pratica-search"
              value={selected && !listOpen ? `${cliName(selected)} — ${selected.tipo_pratica_nome || ""}` : query}
              onChange={(e) => { setQuery(e.target.value); setListOpen(true); if (praticaId) setPraticaId(""); }}
              onFocus={() => setListOpen(true)}
              placeholder="Scrivi il nome del cliente…"
              className="w-full px-2 py-2 bg-transparent text-sm focus:outline-none" />
            {(selected || query) && (
              <button data-testid="qr-pratica-clear" onClick={clearPratica} className="text-[#4a5568] hover:text-[#1A2B4A]"><X className="w-4 h-4" /></button>
            )}
          </div>
          {listOpen && (
            <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-[#1A2B4A]/20 rounded-sm shadow-lg" data-testid="qr-pratica-results">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-sm text-[#4a5568]">Nessuna pratica trovata.</div>
              ) : filtered.map((p) => {
                const saldo = (p.importo_totale || 0) - (p.importo_pagato || 0);
                return (
                  <button key={p.id} data-testid={`qr-pratica-option-${p.id}`} onClick={() => selectPratica(p)}
                    className="w-full text-left px-3 py-2 hover:bg-[#f9f8f5] border-b border-[#1A2B4A]/5 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${p.attiva ? "bg-[#276749]" : "bg-[#1A2B4A]/20"}`} />
                      <span className="text-sm font-medium text-[#1A2B4A] truncate flex-1">{cliName(p)}</span>
                      {saldo > 0 && <span className="text-[11px] text-[#9b2c2c] shrink-0">{eur(saldo)}</span>}
                    </div>
                    <div className="text-xs text-[#4a5568] truncate pl-3.5">{p.tipo_pratica_nome}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Titolo</label>
        <input data-testid="qr-titolo" value={ev.titolo} onChange={(e) => setEv({ ...ev, titolo: e.target.value })} placeholder="es. Mario Rossi — Permesso di Soggiorno"
          className="w-full mt-1 mb-4 px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />

        <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Data e ora (scadenza)</label>
        <input data-testid="qr-data" type="datetime-local" value={ev.data} onChange={(e) => setEv({ ...ev, data: e.target.value })}
          className="w-full mt-1 mb-4 px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />

        <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Avvisami quanti giorni prima</label>
        <div className="flex items-center gap-2 mt-1 mb-4">
          <input data-testid="qr-avvisa" type="number" min="0" value={ev.avvisa} onChange={(e) => setEv({ ...ev, avvisa: e.target.value })}
            className="w-24 px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
          <span className="text-xs text-[#4a5568]">giorni prima (popup + email). 0 = avviso predefinito.</span>
        </div>

        <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Note</label>
        <textarea data-testid="qr-note" rows={2} value={ev.descrizione} onChange={(e) => setEv({ ...ev, descrizione: e.target.value })} placeholder="Note facoltative…"
          className="w-full mt-1 mb-6 px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C] resize-none" />

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-sm hover:bg-[#f9f8f5]">Annulla</button>
          <button data-testid="qr-save" onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-sm hover:bg-[#22345c] disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Crea promemoria
          </button>
        </div>
      </div>
    </div>
  );
}
